import { useState, useEffect, useMemo, useRef } from 'react';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { ADMIN_COLORS, OPEN_PLAY_RECENT_JOINS_LIMIT, SLOT_STATUS_CLOSED, SLOT_CLOSE_REASONS_PATH, ADMIN_BLOCK_BOOKER_NAME, PARTIAL_PAYMENT_BADGE, GRID_BOOKED_CELL_STATUSES } from '../constants/bookingConst';
import { getDatabase, onValue, ref, push, query, orderByChild, equalTo, startAt, endAt, limitToLast, update, get } from 'firebase/database';
import Modal from './Modal';
import { useConfig } from '../context/ConfigContext';
import '../css/AdminBooking.css';

// Import newly extracted sub-components
import AdminTimeSlotSelection from './AdminTimeSlotSelection';
import AdminBookingsList from './AdminBookingsList';
import AdminCleanupBooking from './AdminCleanupBooking';
import AdminReservationDetailModal from './AdminReservationDetailModal';
import PartialPaymentModal from './PartialPaymentModal';
import { sendConfirmationEmail } from '../utils/emailServiceUtil';
import { claimSlotsAtomically, releaseBookingSlots, isBookingActive } from '../utils/bookingAvailabilityUtil';
import { currentAdminEmail, isSuperAdmin } from '../utils/authServiceUtil';
import { releaseEquipmentRental } from '../utils/equipmentAvailabilityUtil';
import { groupBookingRecords, updateGroupStatus, releaseGroupClaims, fetchGroupMembers } from '../utils/bookingGroupUtil';
import { formatTimeSlotRange, getCourtLabel, generateConfirmationId, isBookingFullyPast, isPastDateViewEnabled, sanitizeInitials, getCourtRate, calculateReservationFee, removeSlotsFromBooking, loadingSpinner } from '../utils/bookingUtils';
import { hasReceipt, getBookingReceipt } from '../utils/receiptUtil';
import { setOpenPlayJoinStatus } from '../utils/openPlayUtil';
import { isPackageBooking } from '../utils/packageUtils';
import { getPhNow, phTodayStr } from '../utils/phTime';
import { formatMoney } from '../utils/currencyUtil';
import { buildCloseReasonRecord } from '../utils/slotCloseReasonUtil';
// Downpayments (Config -> Pricing -> "Partial Payment Allowed (%)"). Every
// one of these no-ops when the club has not configured a percentage, which
// is what keeps this screen unchanged for a club that takes full payment.
import {
  getPartialPaymentPercent,
  isPartialPaymentEnabled,
  getReservationTotal,
  getBookingPaymentState,
  buildPartialPaymentFields,
  buildFullPaymentFields,
} from '../utils/partialPaymentUtil';
import {
  logAuditEvent,
  AUDIT_ACTION_TYPES,
  ENTITY_TYPES,
  GRID_STATUS_ACTIONS,
  buildBookingAuditSummary,
  markPastDateEdit,
} from '../utils/auditLogUtil';

/**
 * Reshapes an openPlayJoins record into what the Reservation Queue card and
 * the reservation detail modal expect of a booking, so one queue can carry
 * both without either component special-casing every field it reads.
 *
 * `isOpenPlayJoin` is the flag those two components branch on for the bits
 * that genuinely differ — an Open Play join has players and a per-player
 * fee, not courts and hourly rates, and pricing it through the court
 * schedule would invent numbers the guest was never charged (the same
 * reason event packages get their own branch).
 *
 * @param {object} join - an openPlayJoins record, including its Firebase `id`
 * @returns {object} a queue entry
 */
const openPlayJoinToQueueEntry = (join) => ({
  ...join,
  isOpenPlayJoin: true,
  // Confirm/Decline act on this one record; the array shape mirrors a
  // grouped booking's memberIds so callers don't have to check which is which.
  memberIds: [join.id],
  userName: join.fullName,
  paymentAmount: join.totalAmount,
  bookingFee: 0,
  // No timeSlots: a join claims no court. The empty map still lets the
  // queue's "Current Date" filter match on date, and keeps the hour-counting
  // code in the card from having to guard against a missing `dates`.
  dates: [{ date: join.date, timeSlots: {} }],
});

// Shared jassen light palette (constants/bookingConst.js) — matches the
// "Manage availability" grid view and the rest of the reskinned admin
// shell (header, sidebar, login screen).
const COLORS = ADMIN_COLORS;

// The stored status values are internal enums. Printing one raw ("Status:
// awaiting_payment") tells an admin nothing about what to DO — and "pending"
// is genuinely ambiguous between "waiting on the guest" and "waiting on me",
// which is the distinction that actually matters here. Mirrors the guest-side
// labels in BookingStatusScreen.jsx.
const BOOKING_STATUS_LABELS = {
  awaiting_payment: 'Awaiting payment (guest has not paid yet)',
  pending: 'Pending your approval',
  confirmed: 'Confirmed',
  approved: 'Approved',
  disapproved: 'Declined',
  cancelled: 'Cancelled',
};

/**
 * Human-readable label for a stored booking status, falling back to the raw
 * value so an unrecognised one is still visible rather than blank.
 * @param {string} status
 * @returns {string}
 */
const formatBookingStatus = (status) => BOOKING_STATUS_LABELS[status] || status || 'Unknown';

/**
 * Turns a `bookings` snapshot into the grid's slot map plus a flat list.
 *
 * Extracted so the selected date and the NEXT one produce identical shapes —
 * the after-midnight rows need `bookingId` and `originalData` to open the
 * manage-booking modal, which the guest-side buildBookedSlotsFromSnapshot
 * does not carry.
 *
 * isBookingActive, not a bare status check: it also drops an
 * 'awaiting_payment' booking whose window lapsed with no receipt, which is
 * what keeps this grid agreeing with the guest's about what is really taken.
 *
 * @param {object} snapshot - a Firebase snapshot of bookings for one date
 * @returns {{slots: object, bookingsList: object[]}}
 */
const buildAdminSlotMap = (snapshot) => {
  const slots = {};
  const bookingsList = [];
  if (snapshot && snapshot.exists()) {
    snapshot.forEach((childSnapshot) => {
      const data = childSnapshot.val();
      const bookingId = childSnapshot.key;
      if (isBookingActive(data) && data.timeSlots) {
        Object.entries(data.timeSlots).forEach(([timeSlot, courts]) => {
          const courtList = Array.isArray(courts) ? courts : Object.keys(courts).map(Number);
          courtList.forEach((court) => {
            if (!slots[timeSlot]) slots[timeSlot] = [];
            slots[timeSlot].push({
              court,
              userName: data.userName || 'Unknown',
              status: data.status || 'pending',
              email: data.email || 'pending',
              phone: data.phone || 'pending',
              bookingId,
              originalData: data,
            });
          });
        });
      }
      bookingsList.push({ id: bookingId, ...data });
    });
  }
  return { slots, bookingsList };
};

function AdminBooking() {
  const { config } = useConfig();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookedSlots, setBookedSlots] = useState({});
  // The next date's bookings and overrides, feeding the grid's after-midnight
  // rows (Config -> Pricing -> Open Past Midnight Until).
  const [nextDayBookedSlots, setNextDayBookedSlots] = useState({});
  const [nextDaySlotOverrides, setNextDaySlotOverrides] = useState({});
  const [nextDaySlotCloseReasons, setNextDaySlotCloseReasons] = useState({});
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]); // For list view with date range
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  // "Book New Slot" stages into the grid's pendingChanges rather than
  // writing immediately — the modal lives here but that staging state
  // lives in AdminTimeSlotSelection, so it's reached imperatively (see
  // handleCreateBooking / stageNewBooking).
  const gridRef = useRef(null);
  const [activeTab, setActiveTab] = useState('grid');
  const [showReceipt, setShowReceipt] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  // The receipt image itself, fetched only when the admin asks for it. It is
  // no longer carried on the booking record (see receiptUtil.js), so opening a
  // reservation costs nothing until "View receipt" is actually pressed.
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  // "Manage availability" grid view (jassen UI): per-date admin-closed
  // slots with no real booking, and the global cross-date pending queue.
  const [slotOverrides, setSlotOverrides] = useState({});
  // Why each closed hour on the selected date was closed — the grid's tooltip.
  const [slotCloseReasons, setSlotCloseReasons] = useState({});
  // The queue is fed by two independent listeners — court bookings and Open
  // Play join requests — kept apart so one can't clobber the other's
  // results, then merged into `pendingQueue` below.
  const [pendingBookingQueue, setPendingBookingQueue] = useState([]);
  const [pendingOpenPlayQueue, setPendingOpenPlayQueue] = useState([]);
  const [selectedQueueBooking, setSelectedQueueBooking] = useState(null);
  const [queueActionLoading, setQueueActionLoading] = useState(false);

  // The reservation a downpayment is being taken against, or null when the
  // dialog is closed. Carries everything the write needs so neither the
  // Manage Booking modal nor the Reservation Queue modal has to stay open
  // behind it: { context, guestName, bookingId, entityId, statusOld, record,
  // memberIds, total, resolving }. `resolving` covers the one Firebase read
  // it needs — a multi-date reservation's grand total lives on its PRIMARY
  // record, which a clicked grid cell is not necessarily on.
  const [partialTarget, setPartialTarget] = useState(null);
  const [partialSaving, setPartialSaving] = useState(false);

  // Search and pagination state for list view
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Date range state for list view
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });

  const db = getDatabase();

  const [modalConfig, setModalConfig] = useState({
    isOpen: false, title: '', message: '', type: 'info', confirmText: 'OK', onConfirm: null, showCancel: false, cancelText: 'Cancel'
  });

  const showMsgModal = (config) => setModalConfig({ isOpen: true, ...config });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  // Reset receipt viewer when modal closes. The fetched image is dropped with
  // it — holding it would show the previous reservation's receipt for a beat
  // the next time the modal opens, before its own fetch resolves.
  useEffect(() => {
    if (!showModal) {
      setShowReceipt(false);
      setReceiptImage(null);
      setReceiptLoading(false);
    }
  }, [showModal]);

  /**
   * Loads the selected booking's receipt and reveals the viewer.
   *
   * The image lives at bookingReceipts/{bookingId} rather than on the booking,
   * so it has to be read here. getBookingReceipt() falls back to an inline
   * `paymentScreenshotUrl` for records the backfill has not reached yet.
   *
   * @returns {Promise<void>}
   */
  const handleViewReceipt = async () => {
    setShowReceipt(true);
    // Already in hand from an earlier press in this same modal.
    if (receiptImage) return;

    const { bookingId, originalData } = selectedBooking?.booking || {};
    setReceiptLoading(true);
    try {
      setReceiptImage(await getBookingReceipt(bookingId, originalData));
    } catch (error) {
      console.error('Error loading payment receipt:', error);
      setReceiptImage(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  // Live Sync Effect Hook
  useEffect(() => {
    if (!selectedDate || !db) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const bookingsRef = ref(db, 'bookings');
    const q = query(bookingsRef, orderByChild('date'), equalTo(dateStr));

    const unsubscribe = onValue(q, (snapshot) => {
      const { slots, bookingsList } = buildAdminSlotMap(snapshot);
      setBookedSlots(slots);
      setAllBookings(bookingsList);
    }, (error) => {
      console.error("Listener failed: ", error);
    });

    return () => unsubscribe();
  }, [selectedDate, db]);

  // Manual status overrides for the selected date — lets the admin set a
  // cell's status (e.g. Processing) on a slot with no real booking attached.
  // The admin can set any status regardless of what the cell currently
  // shows, so this simply mirrors whatever the grid last saved.
  useEffect(() => {
    if (!selectedDate || !db) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const overridesRef = ref(db, `slotOverrides/${dateStr}`);
    const unsubscribe = onValue(overridesRef, (snapshot) => {
      setSlotOverrides(snapshot.exists() ? snapshot.val() : {});
    }, (error) => {
      console.error('Slot overrides listener failed:', error);
    });
    // Close reasons for the same date. Tiny node (one short string per
    // closed hour), so a live listener costs next to nothing.
    const unsubReasons = onValue(ref(db, `${SLOT_CLOSE_REASONS_PATH}/${dateStr}`), (snapshot) => {
      setSlotCloseReasons(snapshot.exists() ? snapshot.val() : {});
    }, (error) => {
      console.error('Slot close reasons listener failed:', error);
      setSlotCloseReasons({});
    });
    return () => { unsubscribe(); unsubReasons(); };
  }, [selectedDate, db]);

  // The same two feeds for the FOLLOWING date. The grid's after-midnight rows
  // are real next-date cells, so reading them out of this date's maps would
  // show a different hour's bookings entirely.
  useEffect(() => {
    if (!selectedDate || !db) return undefined;
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    const dateStr = format(d, 'yyyy-MM-dd');

    const unsubBookings = onValue(
      query(ref(db, 'bookings'), orderByChild('date'), equalTo(dateStr)),
      (snap) => setNextDayBookedSlots(buildAdminSlotMap(snap).slots),
      (error) => { console.error('Next-day bookings listener failed:', error); setNextDayBookedSlots({}); },
    );
    const unsubOverrides = onValue(
      ref(db, `slotOverrides/${dateStr}`),
      (snap) => setNextDaySlotOverrides(snap.exists() ? snap.val() : {}),
      (error) => { console.error('Next-day overrides listener failed:', error); setNextDaySlotOverrides({}); },
    );
    const unsubReasons = onValue(
      ref(db, `${SLOT_CLOSE_REASONS_PATH}/${dateStr}`),
      (snap) => setNextDaySlotCloseReasons(snap.exists() ? snap.val() : {}),
      (error) => { console.error('Next-day close reasons listener failed:', error); setNextDaySlotCloseReasons({}); },
    );
    return () => { unsubBookings(); unsubOverrides(); unsubReasons(); };
  }, [selectedDate, db]);

  // Reservation Queue (jassen UI) — every pending booking from today onward,
  // across ALL dates, not just the one currently selected in the grid, so the
  // admin can review and confirm/decline reservations regardless of which day
  // the grid happens to be showing. A multi-date booking is stored as one
  // record PER DATE, all sharing a groupId (see App.jsx's
  // createPendingBooking) — groupBookingRecords collapses those siblings
  // into a single queue card instead of showing one per date.
  //
  // Ordered by `date` with a startAt(today) bound rather than by `status`.
  // Two reasons, and the second is why the bound sits in the query rather
  // than in the callback:
  //
  //   - A booking whose date has already passed can no longer be honoured, so
  //     it is noise in a review queue. Nothing moves a record out of 'pending'
  //     except an admin acting on it, so an abandoned one sat here forever.
  //   - `date` is indexed, so the cut happens SERVER-SIDE and those records
  //     are never downloaded. Filtering them in JS would have cost the same
  //     bandwidth as showing them.
  //
  // Firebase allows only one orderBy per query, which is why 'pending' is
  // then matched here instead of with a second equalTo.
  useEffect(() => {
    if (!db) return;
    const q = query(ref(db, 'bookings'), orderByChild('date'), startAt(phTodayStr()));
    const unsubscribe = onValue(q, (snapshot) => {
      const list = [];
      if (snapshot.exists()) {
        // Statement body, not a concise one: Firebase's DataSnapshot.forEach
        // stops enumerating as soon as the callback returns a TRUTHY value,
        // and `list.push()` returns the new array length.
        snapshot.forEach((childSnapshot) => {
          const booking = { id: childSnapshot.key, ...childSnapshot.val() };
          if (booking.status === 'pending') list.push(booking);
        });
      }
      const grouped = groupBookingRecords(list);
      grouped.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      setPendingBookingQueue(grouped);
    }, (error) => {
      console.error('Reservation queue listener failed:', error);
    });
    return () => unsubscribe();
  }, [db]);

  // Open Play join requests waiting on the same review. They land in the
  // SAME Reservation Queue as court bookings: from the admin's side both
  // are "someone paid, check the receipt and say yes or no", and splitting
  // them across two screens meant a paid guest could sit unreviewed simply
  // because nobody thought to open the other tab.
  //
  // Only 'pending' joins qualify — a join becomes 'pending' the moment its
  // receipt is attached (see openPlayUtil's ACTIVE_JOIN_STATUSES). An
  // unpaid 'pending_payment' join is an abandoned checkout, not a request.
  //
  // Ordered by createdAt with a bound rather than by status, matching
  // AdminOpenPlay.jsx — that reuses the .indexOn ["createdAt"] this node
  // already has instead of needing a second index on status.
  useEffect(() => {
    if (!db) return;
    const q = query(ref(db, 'openPlayJoins'), orderByChild('createdAt'), limitToLast(OPEN_PLAY_RECENT_JOINS_LIMIT));
    const unsubscribe = onValue(q, (snapshot) => {
      const list = [];
      snapshot.forEach((childSnapshot) => {
        const join = { id: childSnapshot.key, ...childSnapshot.val() };
        if (join.status === 'pending') list.push(openPlayJoinToQueueEntry(join));
      });
      list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      setPendingOpenPlayQueue(list);
    }, (error) => {
      console.error('Open Play queue listener failed:', error);
    });
    return () => unsubscribe();
  }, [db]);

  // One queue, oldest request first, so the two sources interleave by when
  // the guest actually submitted rather than clumping by type.
  const pendingQueue = useMemo(
    () => [...pendingBookingQueue, ...pendingOpenPlayQueue]
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)),
    [pendingBookingQueue, pendingOpenPlayQueue],
  );

  // Fetch all bookings for list view with date range filtering. Grouped
  // FIRST (across every date-record, unfiltered) so a multi-date booking's
  // siblings are stitched back into one entry before the range check runs,
  // then a group passes the filter if ANY of its dates falls in range.
  useEffect(() => {
    if (!db || activeTab !== 'list') return;

    const fetchAllBookings = async () => {
      try {
        // Range-queried instead of downloading the whole table. This used to
        // be get(ref(db, 'bookings')) — every booking ever made, with every
        // payment receipt on those records — re-fetched on each date-range
        // tweak. Dates are 'yyyy-MM-dd' strings, so a lexicographic
        // startAt/endAt is a correct inclusive range. Needs .indexOn:
        // ["date"] on /bookings to filter server-side.
        const start = startOfDay(dateRange.startDate);
        const end = endOfDay(dateRange.endDate);
        const bookingsQuery = query(
          ref(db, 'bookings'),
          orderByChild('date'),
          startAt(format(start, 'yyyy-MM-dd')),
          endAt(format(end, 'yyyy-MM-dd')),
        );
        const snapshot = await get(bookingsQuery);

        if (snapshot.exists()) {
          const rawList = [];

          snapshot.forEach((childSnapshot) => {
            rawList.push({ id: childSnapshot.key, ...childSnapshot.val() });
          });

          // A multi-date booking is stored as one record per date, so a group
          // whose other dates fall outside the range came back partial. Those
          // siblings are pulled back in by groupId — a handful of small,
          // targeted queries, versus the whole table this used to download to
          // achieve the same thing. (fetchGroupMembers isn't reused here: it
          // returns {dates, memberIds} for the claim helpers, not the full
          // records groupBookingRecords needs.) Needs .indexOn: ["groupId"].
          const groupIds = [...new Set(rawList.map(r => r.groupId).filter(Boolean))];
          const siblingSnapshots = await Promise.all(groupIds.map(groupId =>
            get(query(ref(db, 'bookings'), orderByChild('groupId'), equalTo(groupId)))
          ));
          const byId = new Map(rawList.map(r => [r.id, r]));
          siblingSnapshots.forEach((siblingSnapshot) => {
            siblingSnapshot.forEach((child) => {
              if (!byId.has(child.key)) byId.set(child.key, { id: child.key, ...child.val() });
            });
          });

          const grouped = groupBookingRecords([...byId.values()]);
          const bookingsList = grouped.filter(booking =>
            (booking.dates || []).some(d => d.date && isWithinInterval(parseISO(d.date), { start, end }))
          );

          // Sort by (first) date, newest first.
          bookingsList.sort((a, b) => new Date(b.date) - new Date(a.date));
          setFilteredBookings(bookingsList);
        } else {
          setFilteredBookings([]);
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setFilteredBookings([]);
      }
    };

    fetchAllBookings();
  }, [db, activeTab, dateRange]);

  // `rowDate` matters: a grid running past midnight shows two dates at
  // once, so the cell's own date has to travel with it or an after-midnight
  // booking is staged against the wrong day. `fromGrid` marks the modal as
  // opened from a cell — the same modal also opens from the reservations
  // list, where there is no Save changes button to defer anything to.
  const handleCellClick = (rowDate, timeSlot, court, booking) => {
    setSelectedBooking({ rowDate, timeSlot, court, booking, fromGrid: true });
    if (booking) {
      setModalType('existing');
    } else {
      setModalType('new');
      setFormData({ fullName: '', email: '', phone: '' });
    }
    setShowModal(true);
  };

  // Handle clicking on a booking from the list view. `booking` may be a
  // grouped multi-date entry (see groupBookingRecords) — its own top-level
  // `timeSlots` only ever reflects the primary (first) date, so read off
  // `dates[0]` explicitly instead. `originalData` still carries the
  // group's groupId, which is all handleCancelBooking/handleConfirmBooking/
  // handleDisapproveBooking need to resolve and cascade across every date.
  const handleListBookingClick = (booking) => {
    // Find the first time slot and court for this booking
    let firstTimeSlot = null;
    let firstCourt = null;

    const firstDateTimeSlots = booking.dates?.[0]?.timeSlots || booking.timeSlots;
    if (firstDateTimeSlots) {
      const entries = Object.entries(firstDateTimeSlots);
      if (entries.length > 0) {
        firstTimeSlot = entries[0][0];
        const courts = entries[0][1];
        const courtList = Array.isArray(courts) ? courts : Object.keys(courts).map(Number);
        firstCourt = courtList[0];
      }
    }

    setSelectedBooking({
      // No row and no grid behind this one — see handleCellClick.
      fromGrid: false,
      timeSlot: firstTimeSlot,
      court: firstCourt,
      booking: {
        bookingId: booking.id,
        userName: booking.userName,
        email: booking.email,
        phone: booking.phone,
        status: booking.status,
        originalData: booking
      }
    });
    setModalType('existing');
    setShowModal(true);
  };

  // ---- Finished reservations are read-only -----------------------------
  //
  // Confirming, declining or cancelling a booking whose dates have all
  // passed rewrites the record of something that already happened, and hands
  // back court hours nobody can rebook. Worse, it is usually a misclick: the
  // reservations list is filtered by date range, so a month of finished
  // bookings sits one row above the live ones.
  //
  // The owner account is exempt. Corrections after the fact are a real need
  // (a no-show recorded wrongly, a refund agreed the next week) — they just
  // shouldn't be one careless click away for everyone with a login.
  //
  // A club that switches on Config -> System -> "View past dates" has opted
  // in to working on finished dates (it already lets staff edit past dates on
  // the grid), so the lock is lifted for every admin there too. With the
  // setting off, only the owner account gets past it.
  //
  // This is a UI-level guard, exactly like CONFIG_ADMIN_EMAILS: it stops the
  // action in the dashboard, but an account with write access to the database
  // could still change the record directly. Enforcing it properly needs a
  // Realtime Database rule keyed on auth.token.email.
  const isOwnerAccount = isSuperAdmin(currentAdminEmail());
  const allowPastBookingActions = isPastDateViewEnabled(config);

  /**
   * Whether the signed-in admin may act on this booking at all. True for the
   * owner account, for every admin when "View past dates" is on, and
   * otherwise only while the booking still has a date today or later.
   * @param {object} booking - a raw record or a grouped list entry
   * @returns {boolean}
   */
  const canActOnBooking = (booking) => isOwnerAccount
    || allowPastBookingActions
    || !isBookingFullyPast(booking);

  /**
   * Refuses the action and says why. Returns true when it refused, so a
   * handler can `if (refuseIfPast(...)) return;` in one line.
   */
  const refuseIfPast = (booking) => {
    if (canActOnBooking(booking)) return false;
    showMsgModal({
      title: 'This reservation has finished',
      message: 'Its dates have already passed, so it can no longer be confirmed, declined or cancelled.\n\n'
        + 'If this one genuinely needs changing, the owner account can still do it, '
        + 'or turn on Config → System → "View past dates" to allow all admins.',
      type: 'warning',
      confirmText: 'OK',
    });
    return true;
  };

  // These three all act on a single grid cell's booking, which may be just
  // one date-record out of a larger multi-date group (see
  // bookingGroupUtil.js) — fetchGroupMembers resolves the *whole* group
  // (falling back to just this one record when it isn't part of a group)
  // so the status change and any slot/equipment release always apply to
  // the entire reservation, not just the date the admin happened to click.
  const handleCancelBooking = async () => {
    if (!selectedBooking?.booking) return;
    if (refuseIfPast(selectedBooking.booking.originalData || selectedBooking.booking)) return;
    setLoading(true);
    try {
      const { bookingId, originalData } = selectedBooking.booking;
      const { dates, memberIds } = await fetchGroupMembers(db, originalData?.groupId, bookingId, originalData?.date);
      await updateGroupStatus(db, memberIds, 'cancelled');
      // Free the slotLocks + equipmentHolds claims too, so both are
      // immediately bookable/rentable again instead of staying locked
      // (this booking had no expiresAt).
      await releaseGroupClaims(db, dates, memberIds);
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_CANCELLED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: originalData?.groupId || bookingId,
        statusOld: originalData?.status || null,
        statusNew: 'cancelled',
        summary: buildBookingAuditSummary(originalData),
      });
      setShowModal(false);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      showMsgModal({ title: 'Firebase Error', message: 'Could not cancel booking: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setLoading(false);
  };

  const handleConfirmBooking = async () => {
    if (!selectedBooking?.booking) return;
    if (refuseIfPast(selectedBooking.booking.originalData || selectedBooking.booking)) return;
    setLoading(true);
    try {
      const { bookingId, originalData } = selectedBooking.booking;
      // Captured before the mutation two lines down overwrites it — this is
      // the audit entry's "old" status.
      const oldStatus = originalData?.status || null;
      const { memberIds } = await fetchGroupMembers(db, originalData?.groupId, bookingId, originalData?.date);
      await updateGroupStatus(db, memberIds, 'confirmed');
      // update booking status to "confirmed"
      selectedBooking.booking.originalData.status = "confirmed";
      // Guarded like every other call site: email is optional on a booking an
      // admin entered for a walk-in, and sending without one fails the
      // backend's validation outright — which used to drop the admin copy too.
      // notifyAdmin is false because an admin clicking Confirm does not need
      // to be emailed about their own click.
      if (selectedBooking.booking.originalData.email) {
        sendConfirmationEmail(selectedBooking.booking.originalData, config?.adminEmail || '', false, config, { notifyAdmin: false }).catch(() => {});
      }
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_CONFIRMED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: originalData?.groupId || bookingId,
        statusOld: oldStatus,
        statusNew: 'confirmed',
        summary: buildBookingAuditSummary(originalData),
      });
      setShowModal(false);
    } catch (error) {
      console.error('Error confirming booking:', error);
      alert('Error confirming booking: ' + error.message);
    }
    setLoading(false);
  };

  // ---- Partial payment (downpayment) ---------------------------------
  //
  // Confirming against part of the total is the SAME business event as a
  // plain confirm — the reservation is on the court either way — so it runs
  // the same group status change, the same guest email and the same audit
  // trail. What it adds is a payment record on every date-record of the
  // reservation (paymentStatus / amountPaid / balanceDue), which is what
  // every PARTIAL badge on this screen reads.
  //
  // Off entirely unless Config -> Pricing carries a percentage: with none,
  // partialEnabled is false, no button renders and none of this is reached.
  const partialPercent = getPartialPaymentPercent(config);
  const partialEnabled = isPartialPaymentEnabled(config);
  // Payment standing of whatever the Manage Booking modal currently has
  // open — drives its PARTIAL line and whether Mark Fully Paid appears.
  // Reads as "not partial" for every booking made before this existed.
  const selectedPaymentState = getBookingPaymentState(
    selectedBooking?.booking?.originalData || selectedBooking?.booking,
  );

  /**
   * Opens the downpayment dialog for whichever reservation is on screen.
   *
   * The dialog opens immediately on a spinner rather than after the read, so
   * the click always produces something; the total it needs is resolved
   * behind it (see partialTarget above).
   *
   * @param {'grid'|'queue'} context - which modal the admin pressed it in.
   * @returns {Promise<void>}
   */
  const handleOpenPartialPayment = async (context) => {
    const isQueue = context === 'queue';
    const record = isQueue
      ? selectedQueueBooking
      : (selectedBooking?.booking?.originalData || selectedBooking?.booking);
    if (!record) return;
    // An Open Play join is priced per player on a session and reserves no
    // court-hours, so there is no reservation total to take a share of.
    if (isQueue && selectedQueueBooking?.isOpenPlayJoin) return;
    if (refuseIfPast(record)) return;

    const bookingId = isQueue ? selectedQueueBooking.id : selectedBooking?.booking?.bookingId;
    if (!bookingId) return;

    setPartialTarget({
      context,
      guestName: record.userName || '',
      bookingId,
      entityId: record.groupId || bookingId,
      statusOld: record.status || null,
      record,
      // Provisional until the read lands: right for a single-date booking,
      // and replaced wholesale for a group.
      memberIds: isQueue ? (selectedQueueBooking.memberIds || [bookingId]) : [bookingId],
      total: getReservationTotal(record),
      resolving: true,
    });

    try {
      const { memberIds, records } = await fetchGroupMembers(db, record.groupId, bookingId, record.date);
      // Guarded against a slow read landing after the admin moved on: only
      // the dialog that asked for this total may receive it.
      setPartialTarget(prev => (prev && prev.bookingId === bookingId
        ? { ...prev, memberIds, total: getReservationTotal(record, records), resolving: false }
        : prev));
    } catch (error) {
      console.error('Could not load this reservation for partial payment:', error);
      setPartialTarget(null);
      showMsgModal({
        title: 'Firebase Error',
        message: 'Could not load this reservation: ' + error.message,
        type: 'error',
        confirmText: 'OK',
      });
    }
  };

  /**
   * Writes the downpayment and confirms the reservation.
   *
   * @param {number} amountPaid - already validated against the club's
   *   minimum by PartialPaymentModal.
   * @returns {Promise<void>}
   */
  const handleConfirmPartialPayment = async (amountPaid) => {
    if (!partialTarget) return;
    const { memberIds, total, record, entityId, statusOld } = partialTarget;
    setPartialSaving(true);
    try {
      const fields = buildPartialPaymentFields(total, amountPaid, partialPercent, getPhNow().toISOString());
      await updateGroupStatus(db, memberIds, 'confirmed', fields);
      // Guarded like every other call site: email is optional on a booking
      // an admin entered for a walk-in. notifyAdmin is false — the admin
      // does not need emailing about their own click.
      // The email gets the record AS JUST SAVED — the payment fields and the
      // reservation total — not the copy read before the write, so it can
      // state the amount paid and the balance instead of only the total.
      if (record.email) {
        sendConfirmationEmail(
          { ...record, ...fields, paymentAmount: total, status: 'confirmed' },
          config?.adminEmail || '', false, config, { notifyAdmin: false },
        ).catch(() => {});
      }
      const settled = fields.balanceDue <= 0;
      logAuditEvent({
        // An admin who typed the full total into the partial field has
        // simply confirmed it — logging that as a partial payment would put
        // a reservation that owes nothing in the "who is still owing?" list.
        actionType: settled ? AUDIT_ACTION_TYPES.BOOKING_CONFIRMED : AUDIT_ACTION_TYPES.BOOKING_PARTIALLY_PAID,
        entityType: ENTITY_TYPES.BOOKING,
        entityId,
        statusOld,
        statusNew: 'confirmed',
        changes: { total, amountPaid: fields.amountPaid, balanceDue: fields.balanceDue },
        summary: buildBookingAuditSummary(record),
      });
      setPartialTarget(null);
      setShowModal(false);
      setSelectedQueueBooking(null);
      showMsgModal({
        title: 'Booking Confirmed',
        message: settled
          ? `Confirmed and fully paid (${formatMoney(fields.amountPaid, config)}).`
          : `Confirmed with a partial payment of ${formatMoney(fields.amountPaid, config)}. ${formatMoney(fields.balanceDue, config)} is still due.`,
        type: 'success',
        confirmText: 'OK',
      });
    } catch (error) {
      console.error('Error recording the partial payment:', error);
      showMsgModal({
        title: 'Firebase Error',
        message: 'Could not record this payment: ' + error.message,
        type: 'error',
        confirmText: 'OK',
      });
    }
    setPartialSaving(false);
  };

  /**
   * Settles the balance on a partially-paid reservation. The booking's own
   * status is left exactly as it is — this is a payment event, not a status
   * change.
   *
   * @returns {Promise<void>}
   */
  const handleMarkFullyPaid = async () => {
    const record = selectedBooking?.booking?.originalData || selectedBooking?.booking;
    if (!record) return;
    if (refuseIfPast(record)) return;
    setLoading(true);
    try {
      const bookingId = selectedBooking.booking.bookingId;
      const { memberIds, records } = await fetchGroupMembers(db, record.groupId, bookingId, record.date);
      const total = getReservationTotal(record, records);
      const fields = buildFullPaymentFields(total, getPhNow().toISOString());
      await updateGroupStatus(db, memberIds, record.status || 'confirmed', fields);
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_FULLY_PAID,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: record.groupId || bookingId,
        changes: {
          total,
          amountPaid: fields.amountPaid,
          balanceDue: 0,
          // What was outstanding before this click — the figure that makes
          // the entry worth reading later.
          settledBalance: getBookingPaymentState(record).balanceDue,
        },
        summary: buildBookingAuditSummary(record),
      });
      setShowModal(false);
      showMsgModal({
        title: 'Balance Settled',
        message: `This reservation is now fully paid (${formatMoney(total, config)}).`,
        type: 'success',
        confirmText: 'OK',
      });
    } catch (error) {
      console.error('Error settling the balance:', error);
      showMsgModal({
        title: 'Firebase Error',
        message: 'Could not settle this balance: ' + error.message,
        type: 'error',
        confirmText: 'OK',
      });
    }
    setLoading(false);
  };

  const handleDisapproveBooking = async () => {
    if (!selectedBooking?.booking) return;
    if (refuseIfPast(selectedBooking.booking.originalData || selectedBooking.booking)) return;
    setLoading(true);
    try {
      const { bookingId, originalData } = selectedBooking.booking;
      const { dates, memberIds } = await fetchGroupMembers(db, originalData?.groupId, bookingId, originalData?.date);
      await updateGroupStatus(db, memberIds, 'disapproved');
      await releaseGroupClaims(db, dates, memberIds);
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_DECLINED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: originalData?.groupId || bookingId,
        statusOld: originalData?.status || null,
        statusNew: 'disapproved',
        summary: buildBookingAuditSummary(originalData),
      });
      setShowModal(false);
    } catch (error) {
      console.error('Error disapproving booking:', error);
      alert('Error disapproving booking: ' + error.message);
    }
    setLoading(false);
  };

  // Applies a batch of "Manage availability" grid edits at once (the grid
  // view stages changes locally and only calls this once the admin taps
  // "Save changes"). The admin can set any status on any cell regardless
  // of its current status. Each change either updates an existing
  // booking's status, or sets a manual status override for a slot that
  // has no real booking attached.
  const handleSaveGridChanges = async (changes, dateForChanges) => {
    const dateStr = format(dateForChanges, 'yyyy-MM-dd');
    // A grid that runs past midnight stages cells from TWO dates, so each
    // change carries its own. Falling back to the viewed date keeps every
    // pre-existing caller (and any change staged before this existed) working
    // exactly as before.
    const dateOf = (change) => change?.dateStr || dateStr;
    // Past dates are saved exactly like any other date — the grid lets an
    // admin set any status there (reaching past dates at all is gated by
    // Config -> System -> "View past dates").
    const updates = {};
    const toConfirmEmails = [];
    // Bookings cancelled by this batch — their slotLocks claims need to be
    // released afterward so those slots are immediately reclaimable rather
    // than staying locked (a confirmed/pending booking has no expiresAt).
    const bookingIdsToRelease = new Map();
    // One audit entry per booking whose status this batch actually changed —
    // keyed by bookingId so a booking spanning several cells (multiple
    // timeslots/courts, all part of one grid save) logs once, not once per
    // cell.
    const gridStatusChanges = new Map();
    // bookingId -> { booking, dateStr, cells } for hours this save takes off
    // an existing reservation. Resolved after the cell loop (see above).
    const releasedByBooking = new Map();

    // Staged "Book New Slot" entries are real booking records, not status
    // overrides, so they're created first (each needs its own atomic slot
    // claim) and then skipped by the override pass below.
    const stagedBookings = changes
      .filter(c => c.newBooking)
      .map(change => ({ ...change, dateStr: dateOf(change) }));
    const failures = [];
    // ONE reservation per guest, not per cell. The modal captures a single
    // hour at a time, so an admin booking a 3-hour walk-in opens it three
    // times — and those three cells are one reservation. Grouped on the guest
    // rather than merged wholesale so two different walk-ins saved together
    // still get a booking each.
    const guestGroups = new Map();
    stagedBookings.forEach((change) => {
      const details = change.newBooking;
      const identity = [
        (details.fullName || '').trim().toLowerCase(),
        (details.email || '').trim().toLowerCase(),
        (details.phone || '').trim(),
      ].join('|');
      if (!guestGroups.has(identity)) guestGroups.set(identity, { details, cells: [] });
      guestGroups.get(identity).cells.push(change);
    });
    for (const { details, cells } of guestGroups.values()) {
      // eslint-disable-next-line no-await-in-loop
      const result = await createGridBooking(cells, {
        userName: details.fullName,
        email: details.email,
        phone: details.phone,
        adminBlock: false,
      });
      if (!result.ok) failures.push(result.reason);
    }

    // Setting an EMPTY cell to BOOKED creates a REAL booking held by the
    // club, not a slotOverrides string. The string was admin-side only: the
    // guest grid never read it and the checkout race check never tested it,
    // so an hour the admin had marked BOOKED could still be booked out from
    // under them. A booking record holds the slot through the same lock every
    // guest checkout goes through, so it cannot.
    const blockKey = (dateStr_, timeSlot, court) => `${dateStr_}|${timeSlot}|${court}`;
    const adminBlocks = changes
      .filter(({ newBooking, booking, originalStatus, newStatus }) => (
        !newBooking
        && newStatus === 'booked'
        // Cells that already carry a booking take the status-update path
        // below; only genuinely empty ones need a record creating.
        && !(GRID_BOOKED_CELL_STATUSES.includes(originalStatus) && booking?.bookingId)
      ))
      // dateOf is only in scope here, so each cell's date is resolved now and
      // the creator works off plain data.
      .map(change => ({ ...change, dateStr: dateOf(change) }));
    // Cells the booking path owns. They are kept out of the override pass
    // below whether the booking was written or not: a written one already
    // holds the hour, and a FAILED one must not fall through to the
    // slotOverrides string, which would leave the grid claiming an hour is
    // BOOKED while the guest screen still offers it.
    const blockedCells = new Set();
    // ONE booking for the whole save, not one per cell: an admin filling in
    // three hours for the same walk-in is recording one reservation, and
    // billing it as three would charge a flat processing fee three times.
    // Every cell rides on the same record (see createGridBooking).
    if (adminBlocks.length > 0) {
      // Distinct initials in the order they were typed. Several walk-ins can
      // land in one save and the record has one name, so joining them keeps
      // every name the admin entered instead of dropping all but the first.
      // None typed at all means the hours were taken off the market from the
      // dropdown, which is the club holding its own court.
      const initials = [...new Set(
        adminBlocks.map(c => sanitizeInitials(c.adminInitials)).filter(Boolean)
      )];
      const result = await createGridBooking(adminBlocks, {
        userName: initials.join('/') || ADMIN_BLOCK_BOOKER_NAME,
        // No contact details to record and nobody to email — this is the club
        // booking on a guest's behalf, so the confirmation path is skipped.
        email: '',
        phone: '',
        adminBlock: true,
      });
      if (!result.ok) failures.push(result.reason);
      adminBlocks.forEach(({ dateStr: cellDate, timeSlot, court }) => {
        blockedCells.add(blockKey(cellDate, timeSlot, court));
        if (!result.ok) return;
        // A cell coming from CLOSED (or any other override) still carries
        // that string; left behind, the guest grid would go on blocking the
        // hour as "closed" instead of showing it booked.
        updates[`slotOverrides/${cellDate}/${timeSlot}/${court}`] = null;
      });
    }

    changes
      // Cells handled above must not also get an override written, or the
      // stale string would sit on top of the booking that now holds the hour.
      .filter(c => !c.newBooking && !blockedCells.has(blockKey(dateOf(c), c.timeSlot, c.court)))
      .forEach((change) => {
      const { timeSlot, court, booking, originalStatus, newStatus } = change;
      const changeDate = dateOf(change);
      // 'partial' counts: the cell carries a real, confirmed booking that
      // simply has a balance on it. Reading it as an empty cell would write
      // a slotOverrides string over a live reservation.
      const hadBooking = GRID_BOOKED_CELL_STATUSES.includes(originalStatus);

      if (hadBooking && booking?.bookingId) {
        let dbStatus;
        if (newStatus === 'booked') {
          dbStatus = 'confirmed';
          updates[`bookings/${booking.bookingId}/status`] = dbStatus;
          if (originalStatus === 'processing' && booking.originalData) {
            toConfirmEmails.push(booking.originalData);
          }
        } else if (newStatus === 'processing') {
          dbStatus = 'pending';
          updates[`bookings/${booking.bookingId}/status`] = dbStatus;
        } else {
          // Moving a booked/processing cell to any other status frees THIS
          // HOUR. It used to cancel the entire reservation, which meant
          // releasing one hour of a three-hour booking threw away the other
          // two. Collected here and resolved after the loop instead: several
          // cells of one booking can be released in the same save, and what
          // is left of the record can only be worked out once all of them
          // are known.
          if (!releasedByBooking.has(booking.bookingId)) {
            releasedByBooking.set(booking.bookingId, { booking, dateStr: changeDate, cells: [] });
          }
          releasedByBooking.get(booking.bookingId).cells.push({ timeSlot, court });
          if (newStatus !== 'open') {
            updates[`slotOverrides/${changeDate}/${timeSlot}/${court}`] = newStatus;
          }
        }
        // Only a real status move is logged as one; an hour coming off a
        // booking that survives is not a status change and logs its own
        // BOOKING_SLOT_REMOVED entry below.
        if (dbStatus && !gridStatusChanges.has(booking.bookingId)) {
          gridStatusChanges.set(booking.bookingId, {
            statusOld: booking.originalData?.status || booking.status || null,
            statusNew: dbStatus,
            originalData: booking.originalData || booking,
            dateStr: changeDate,
          });
        }
      } else {
        // No real booking on this cell — set (or clear) the manual override.
        updates[`slotOverrides/${changeDate}/${timeSlot}/${court}`] = newStatus === 'open' ? null : newStatus;
      }
    });

    // ---- Hours released from existing bookings --------------------------
    //
    // An hour coming off a reservation is not the reservation being
    // cancelled. The record keeps its identity, its guest and its remaining
    // hours; only once NOTHING is left does it actually get cancelled.
    const slotRemovalAudits = [];
    releasedByBooking.forEach(({ booking, dateStr: releaseDate, cells }, bookingId) => {
      const original = booking.originalData || {};
      const remaining = removeSlotsFromBooking(original.timeSlots, cells);
      // The hours actually taken off, in the same shape a booking stores, so
      // the audit summary renders them through the existing renderer.
      const removed = {};
      cells.forEach(({ timeSlot, court }) => {
        removed[timeSlot] = [...(removed[timeSlot] || []), Number(court)];
      });

      if (Object.keys(remaining).length === 0) {
        // Nothing left to hold — this is a cancellation after all, and takes
        // the path it always did.
        updates[`bookings/${bookingId}/status`] = 'cancelled';
        // Keyed by booking id -> the date whose locks it holds: a batch can
        // cancel bookings on two dates at once, and releasing both against
        // the viewed date would leave tomorrow's slot locked forever.
        bookingIdsToRelease.set(bookingId, releaseDate);
        if (!gridStatusChanges.has(bookingId)) {
          gridStatusChanges.set(bookingId, {
            statusOld: original.status || booking.status || null,
            statusNew: 'cancelled',
            originalData: original,
            dateStr: releaseDate,
          });
        }
        return;
      }

      updates[`bookings/${bookingId}/timeSlots`] = remaining;
      // Only the freed cells' claims come off. releaseBookingSlots would drop
      // every lock this booking holds, which on a booking that still has
      // hours left would hand them to the next guest to check out.
      cells.forEach(({ timeSlot, court }) => {
        updates[`slotLocks/${releaseDate}/${timeSlot}/${court}`] = null;
      });

      // Re-priced, or the record goes on charging for hours it no longer
      // has. A package is the exception: it is sold as one all-in block, not
      // by the hour, so its price is left exactly as the club set it.
      if (!isPackageBooking(original)) {
        const courtTotal = Object.entries(remaining).reduce((sum, [slot, courtList]) => (
          sum + courtList.reduce((s, court) => (
            s + (Number(getCourtRate(config, court, slot, original.sportId, releaseDate)) || 0)
          ), 0)
        ), 0);
        // A multi-date booking's fee is charged once across the whole group
        // and lives on the primary record, so it cannot be recomputed from
        // one date's hours — that record keeps the fee it was charged.
        const isGrouped = !!original.groupId;
        const fee = isGrouped
          ? (Number(original.bookingFee) || 0)
          : calculateReservationFee(config, remaining);
        // Equipment is charged once for the reservation and rides on the same
        // record as the fee — preserved rather than recomputed, since the
        // hours coming off do not return the rented gear.
        const equipmentTotal = Number(original.equipmentRental?.total) || 0;
        // Mirrors how the booking was priced when written: on a single record
        // (or a group's primary) paymentAmount carries the shared costs, and
        // every other member carries only its own court subtotal.
        const carriesSharedCosts = !isGrouped || original.groupPrimary === true;
        updates[`bookings/${bookingId}/paymentAmount`] = carriesSharedCosts
          ? courtTotal + fee + equipmentTotal
          : courtTotal;
        if (!isGrouped) updates[`bookings/${bookingId}/bookingFee`] = fee;
      }

      slotRemovalAudits.push({ bookingId, original, releaseDate, removed });
    });

    // Close reasons ride in the same atomic update as the overrides they
    // explain. Written for every cell the override pass handled (booked or
    // empty) that lands on CLOSED, and cleared when a closed hour reopens so
    // a stale reason never resurfaces the next time that hour is closed.
    changes
      .filter(c => !c.newBooking && !blockedCells.has(blockKey(dateOf(c), c.timeSlot, c.court)))
      .forEach((change) => {
        const { timeSlot, court, originalStatus, newStatus, closeReason } = change;
        const reasonPath = `${SLOT_CLOSE_REASONS_PATH}/${dateOf(change)}/${timeSlot}/${court}`;
        if (newStatus === SLOT_STATUS_CLOSED) {
          updates[reasonPath] = closeReason ? buildCloseReasonRecord(closeReason) : null;
        } else if (originalStatus === SLOT_STATUS_CLOSED) {
          updates[reasonPath] = null;
        }
      });
    // An hour going from CLOSED straight to BOOKED takes the booking path
    // above, which clears its override once the booking is written; its
    // reason goes with it (only then — a failed booking keeps the hour closed).
    adminBlocks.forEach(({ dateStr: cellDate, timeSlot, court, originalStatus }) => {
      const cellPath = `slotOverrides/${cellDate}/${timeSlot}/${court}`;
      if (originalStatus === SLOT_STATUS_CLOSED && cellPath in updates && updates[cellPath] === null) {
        updates[`${SLOT_CLOSE_REASONS_PATH}/${cellDate}/${timeSlot}/${court}`] = null;
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(ref(db), updates);
    }

    // One entry per booking an hour came off, saying WHICH hours went. The
    // booking's own status is untouched, so this deliberately isn't a
    // BOOKING_CANCELLED — see BOOKING_SLOT_REMOVED in bookingConst.js.
    slotRemovalAudits.forEach(({ bookingId, original, releaseDate, removed }) => {
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_SLOT_REMOVED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: original.groupId || bookingId,
        summary: markPastDateEdit(buildBookingAuditSummary({
          ...original,
          // The hours REMOVED, not the ones that remain — the trail answers
          // "what came off this booking?".
          dates: [{ date: releaseDate, timeSlots: removed }],
        }), [releaseDate]),
      });
    });

    gridStatusChanges.forEach(({ statusOld, statusNew, originalData, dateStr: changedDate }, bookingId) => {
      // The action type is derived from the status the cell actually landed
      // on, NOT hardcoded — confirming a booking from this grid is the same
      // business event as confirming it from the queue or the Manage Booking
      // modal, and has to log as the same action or it goes missing from the
      // Audit Trail's "Booking confirmed" filter and its Confirmed count.
      // BOOKING_STATUS_CHANGED stays as the fallback for a move that has no
      // more specific meaning (e.g. back to pending/processing).
      logAuditEvent({
        actionType: GRID_STATUS_ACTIONS[statusNew] || AUDIT_ACTION_TYPES.BOOKING_STATUS_CHANGED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: originalData?.groupId || bookingId,
        statusOld,
        statusNew,
        summary: markPastDateEdit(buildBookingAuditSummary(originalData), [changedDate]),
      });
    });

    // Closing an hour (and reopening one) is an availability decision in its
    // own right, so it gets its own entry against the SLOT rather than a
    // booking: an EMPTY cell can be closed, and that used to leave no trace
    // anywhere. When a booking was sitting on the hour, its cancellation
    // still logs its own BOOKING_CANCELLED entry above — the two together
    // say what happened and in what order.
    //
    // Grouped by court and by the transition, so one save closing six hours
    // on Court 2 is a single entry listing six hours (rendered as a range by
    // the trail's formatSlotRanges) rather than six near-identical rows —
    // the same compression the booking entries above already get.
    const slotAudits = new Map();
    changes.filter(c => !c.newBooking).forEach((change) => {
      const { timeSlot, court, originalStatus, newStatus, closeReason } = change;
      const changeDate = dateOf(change);
      const closing = newStatus === SLOT_STATUS_CLOSED;
      const reopening = originalStatus === SLOT_STATUS_CLOSED && newStatus !== SLOT_STATUS_CLOSED;
      // Any other status set on an EMPTY hour (open/processing/passed) only
      // writes a slotOverrides string, which used to leave no trace. Hours
      // with a booking are covered by the booking entries above, and a free
      // hour going to BOOKED logs its new booking's BOOKING_SUBMITTED.
      const emptyCellChange = !closing && !reopening
        && newStatus !== originalStatus
        && newStatus !== 'booked'
        && !GRID_BOOKED_CELL_STATUSES.includes(originalStatus);
      if (!closing && !reopening && !emptyCellChange) return;
      let actionType = AUDIT_ACTION_TYPES.SLOT_STATUS_CHANGED;
      if (closing) actionType = AUDIT_ACTION_TYPES.SLOT_CLOSED;
      else if (reopening) actionType = AUDIT_ACTION_TYPES.SLOT_REOPENED;
      // The old status is part of the key, not just the payload: closing an
      // already-booked hour and closing an empty one are different events
      // and must not be merged into a row claiming one old status for both.
      // The DATE is in the key for the same reason — one save can close hours
      // on tonight and on tomorrow morning, and merging those would produce
      // an entry claiming one date for both.
      // The reason is in the key too: hours closed for different reasons in
      // one save are separate decisions and must each keep their own reason.
      const reason = closing ? (closeReason || '') : '';
      const key = `${actionType}|${changeDate}|${court}|${originalStatus}|${newStatus}|${reason}`;
      if (!slotAudits.has(key)) {
        slotAudits.set(key, { actionType, dateStr: changeDate, court, originalStatus, newStatus, reason, timeSlots: [] });
      }
      slotAudits.get(key).timeSlots.push(timeSlot);
    });

    slotAudits.forEach(({ actionType, dateStr: auditDate, court, originalStatus, newStatus, reason, timeSlots }) => {
      logAuditEvent({
        actionType,
        entityType: ENTITY_TYPES.SLOT,
        // The admin's close reason — shown on the Audit Trail under the entry.
        reason: reason || null,
        // A slot has no Firebase key of its own, so the path it was written
        // to stands in as the id. It leads with the date, which is what the
        // Audit Trail's search box is most likely to be given.
        entityId: `${auditDate}/court-${court}`,
        statusOld: originalStatus,
        statusNew: newStatus,
        // Same { date, slotsByCourt } shape a booking summary uses, so the
        // trail's Details column renders it through the existing renderer
        // with no special case: "2026-08-24 / Center Court: 6:00 - 9:00 AM".
        summary: markPastDateEdit({
          date: auditDate,
          dates: [auditDate],
          slotsByCourt: { [court]: timeSlots },
        }, [auditDate]),
      });
    });

    // Surfaced after the rest of the batch has been applied, so one lost
    // race doesn't discard the admin's other edits.
    if (failures.length > 0) {
      throw new Error(`${failures.join(' ')} Please re-check those slots.`);
    }

    // Confirming is an admin action, so only the guest is told (notifyAdmin
    // false); and a booking with no guest email has nobody to tell.
    toConfirmEmails.forEach(bookingData => {
      if (!bookingData.email) return;
      sendConfirmationEmail({ ...bookingData, status: 'confirmed' }, config?.adminEmail || '', false, config, { notifyAdmin: false }).catch(() => {});
    });

    bookingIdsToRelease.forEach((releaseDate, bookingId) => {
      releaseBookingSlots(db, releaseDate, bookingId).catch(() => {});
      releaseEquipmentRental(db, releaseDate, bookingId).catch(() => {});
    });
  };

  // Reservation Queue detail modal — Confirm / Decline act directly on the
  // booking clicked from the queue (independent of whatever the grid's
  // selectedDate happens to be, since queue items can be for any date).
  // selectedQueueBooking is a grouped entry (see groupBookingRecords) — its
  // memberIds carry every date-record's own id for a multi-date booking (or
  // just the one id for an ordinary single-date booking), so Confirm/
  // Decline always apply to the whole reservation at once.
  const handleQueueConfirm = async () => {
    if (!selectedQueueBooking) return;
    if (refuseIfPast(selectedQueueBooking)) return;
    setQueueActionLoading(true);
    try {
      // An Open Play join owns no slots and no group, so none of the
      // booking machinery below applies — its whole state is one status
      // field, and flipping it to 'confirmed' is what puts the guest on the
      // session's player list for good.
      if (selectedQueueBooking.isOpenPlayJoin) {
        await setOpenPlayJoinStatus(selectedQueueBooking.id, 'confirmed');
        setSelectedQueueBooking(null);
        showMsgModal({
          title: 'Open Play Join Confirmed',
          message: `${selectedQueueBooking.userName} is confirmed for this Open Play session.`,
          type: 'success',
          confirmText: 'OK',
        });
        setQueueActionLoading(false);
        return;
      }
      await updateGroupStatus(db, selectedQueueBooking.memberIds, 'confirmed');
      // Guest only — the admin just performed this confirmation themselves.
      if (selectedQueueBooking.email) {
        sendConfirmationEmail({ ...selectedQueueBooking, status: 'confirmed' }, config?.adminEmail || '', false, config, { notifyAdmin: false }).catch(() => {});
      }
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_CONFIRMED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: selectedQueueBooking.groupId || selectedQueueBooking.id,
        statusOld: selectedQueueBooking.status || null,
        statusNew: 'confirmed',
        summary: buildBookingAuditSummary(selectedQueueBooking),
      });
      setSelectedQueueBooking(null);
      showMsgModal({ title: 'Booking Confirmed', message: 'This booking is now confirmed.', type: 'success', confirmText: 'OK' });
    } catch (error) {
      console.error('Error confirming reservation:', error);
      showMsgModal({ title: 'Firebase Error', message: 'Could not confirm reservation: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setQueueActionLoading(false);
  };

  const handleQueueDecline = async () => {
    if (!selectedQueueBooking) return;
    if (refuseIfPast(selectedQueueBooking)) return;
    setQueueActionLoading(true);
    try {
      // Declining drops the join out of ACTIVE_JOIN_STATUSES, which removes
      // those players from the session's roster and hands their spots back
      // to the cap — no slot claims to release, unlike a court booking.
      if (selectedQueueBooking.isOpenPlayJoin) {
        await setOpenPlayJoinStatus(selectedQueueBooking.id, 'declined');
        setSelectedQueueBooking(null);
        showMsgModal({
          title: 'Open Play Join Declined',
          message: `${selectedQueueBooking.numPlayers} player${selectedQueueBooking.numPlayers === 1 ? '' : 's'} removed from the session — those spots are open again.`,
          type: 'info',
          confirmText: 'OK',
        });
        setQueueActionLoading(false);
        return;
      }
      await updateGroupStatus(db, selectedQueueBooking.memberIds, 'disapproved');
      await releaseGroupClaims(db, selectedQueueBooking.dates, selectedQueueBooking.memberIds);
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKING_DECLINED,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: selectedQueueBooking.groupId || selectedQueueBooking.id,
        statusOld: selectedQueueBooking.status || null,
        statusNew: 'disapproved',
        summary: buildBookingAuditSummary(selectedQueueBooking),
      });
      setSelectedQueueBooking(null);
      showMsgModal({ title: 'Booking Declined', message: "You've declined this booking, the selected schedules will now be RE-OPENED.", type: 'info', confirmText: 'OK' });
    } catch (error) {
      console.error('Error declining reservation:', error);
      showMsgModal({ title: 'Firebase Error', message: 'Could not decline reservation: ' + error.message, type: 'error', confirmText: 'OK' });
    }
    setQueueActionLoading(false);
  };

  /**
   * Takes ONE hour off the booking on this cell — staged, not written.
   *
   * Cancelling used to fire straight at Firebase the moment the button was
   * pressed, which made it the only grid action a mis-click could not be
   * walked back from. It now stages the same edit the cell's dropdown stages
   * for OPEN, so it joins the unsaved count and lands with everything else on
   * Save changes (or is thrown away by Clear changes).
   *
   * Removes the hour rather than the reservation: handleSaveGridChanges drops
   * just this cell from the record and only cancels the booking outright once
   * nothing is left on it.
   *
   * @returns {void}
   */
  const handleStageCancelHour = () => {
    if (!selectedBooking?.booking) return;
    if (refuseIfPast(selectedBooking.booking.originalData || selectedBooking.booking)) return;
    gridRef.current?.stageCellRelease(
      selectedBooking.rowDate,
      selectedBooking.timeSlot,
      selectedBooking.court,
      selectedBooking.booking,
    );
    setShowModal(false);
  };

  // "Book New Slot" no longer writes to Firebase on click — it stages the
  // booking in the grid alongside every other pending edit, so nothing is
  // persisted until the admin presses "Save changes" (and "Clear changes"
  // discards it). The actual create happens in handleSaveGridChanges.
  const handleCreateBooking = () => {
    // Only the name is required — an admin booking a walk-in often has
    // nothing else to go on, and email/phone stay optional so they can't
    // block the booking.
    if (!formData.fullName?.trim()) {
      showMsgModal({ title: 'Missing Information', message: 'Please enter the guest\'s full name.', type: 'warning', confirmText: 'OK' });
      return;
    }
    gridRef.current?.stageNewBooking(selectedBooking.timeSlot, selectedBooking.court, {
      fullName: formData.fullName.trim(),
      // Normalised to '' rather than left undefined — Firebase strips
      // undefined values, which would make these fields absent instead of
      // empty on the saved booking.
      email: (formData.email || '').trim(),
      phone: (formData.phone || '').trim(),
    }, selectedBooking.rowDate);
    setShowModal(false);
  };

  /**
   * Writes ONE reservation covering every grid cell passed in.
   *
   * Both ways of booking from the grid land here — the initials badge on an
   * open cell, and the Book New Slot modal — because both have the same
   * problem: they capture ONE hour at a time. An admin taking a 3-hour
   * walk-in types in three cells, or opens the modal three times, and that is
   * one reservation, not three. Writing a record per cell recorded it as
   * three 1-hour bookings, which is what made an export show a single hour at
   * a single hour's rate, and charged a flat processing fee once per hour.
   *
   * Priced at the hours' own rates via getCourtRate — date- and sport-aware,
   * so a weekend band or a promo hour costs what the guest grid says it
   * costs.
   *
   * A save spanning midnight stages cells on two dates, and a booking record
   * holds one date — so that becomes one record per date stitched together by
   * a shared confirmationId and groupId, exactly as a guest's multi-date
   * booking is stored (see bookingGroupUtil.js). Only the primary record's
   * paymentAmount carries the processing fee, or summing the group would
   * count it twice.
   *
   * Goes through claimSlotsAtomically like any other booking: an admin and a
   * guest can race for the same hour, and the loser has to find out here
   * rather than by both holding it. The records and their slot locks are ONE
   * atomic write, so a conflict on ANY hour fails the whole save with nothing
   * written — a walk-in booked for three hours who can only have two is a
   * decision for the admin, not something to silently half-write.
   *
   * @param {Array<{dateStr: string, timeSlot: string, court: number,
   *   sportId?: string}>} cells - The hours this reservation covers.
   * @param {{userName: string, email: string, phone: string,
   *   adminBlock: boolean}} guest - Who the hours are held for.
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  const createGridBooking = async (cells, guest) => {
    if (cells.length === 0) return { ok: true };

    // date -> { timeSlot: [courts] }, the shape every booking record, slot
    // claim and fee helper already speaks.
    const byDate = new Map();
    cells.forEach(({ dateStr, timeSlot, court }) => {
      if (!byDate.has(dateStr)) byDate.set(dateStr, {});
      const slots = byDate.get(dateStr);
      slots[timeSlot] = [...(slots[timeSlot] || []), Number(court)];
    });

    // The grid only ever shows one sport's courts at a time, so every cell in
    // a save carries the same one; the first that has it stands for all.
    const sportId = cells.find(c => c.sportId)?.sportId || null;

    const dateList = [...byDate.keys()].sort();
    const isMultiDate = dateList.length > 1;
    const dateRecords = dateList.map((dateStr, idx) => {
      const docRef = push(ref(db, 'bookings'));
      return { dateStr, bookingId: docRef.key, timeSlots: byDate.get(dateStr), groupPrimary: idx === 0 };
    });
    const groupId = isMultiDate ? dateRecords[0].bookingId : null;

    // Priced per hour at the rate that applies on that hour's own date, the
    // same call the guest checkout and the export use — so the three agree.
    const perDateCourtTotal = {};
    let courtTotal = 0;
    dateRecords.forEach((rec) => {
      let dateTotal = 0;
      Object.entries(rec.timeSlots).forEach(([timeSlot, courtList]) => {
        courtList.forEach((court) => {
          dateTotal += Number(getCourtRate(config, court, timeSlot, sportId, rec.dateStr)) || 0;
        });
      });
      perDateCourtTotal[rec.dateStr] = dateTotal;
      courtTotal += dateTotal;
    });
    // Flat by default, or multiplied by every court-hour in the reservation
    // when Config -> Pricing -> "Charge per hour" is ticked. Computed over the
    // whole selection for the same reason the guest flow does it that way.
    const bookingFee = calculateReservationFee(config, Object.fromEntries(byDate));
    const grandTotal = courtTotal + bookingFee;

    const confirmationId = generateConfirmationId();
    const createdAt = getPhNow().toISOString();
    // Same link the guest's own checkout writes, and what the confirmation
    // email below turns into the "track your booking" button — an admin-made
    // booking was emailing that button with nothing behind it.
    const trackingUrl = `${window.location.origin}/bookings/${confirmationId}`;
    const writes = {};
    dateRecords.forEach((rec) => {
      writes[`bookings/${rec.bookingId}`] = {
        confirmationId,
        sportId,
        ...(isMultiDate ? { groupId, groupPrimary: rec.groupPrimary, groupDateCount: dateRecords.length } : {}),
        date: rec.dateStr,
        timeSlots: rec.timeSlots,
        userName: guest.userName,
        email: guest.email || '',
        phone: guest.phone || '',
        // Only the primary record carries the processing fee in its
        // paymentAmount; the rest carry their own date's court subtotal, so
        // summing the group can't double-count it.
        paymentAmount: rec.groupPrimary ? grandTotal : perDateCourtTotal[rec.dateStr],
        bookingFee,
        trackingUrl,
        // Confirmed on creation, where a guest's record starts at
        // 'awaiting_payment' — the club is entering a booking it has already
        // taken, so there is no payment window to count down and no
        // paymentDeadline to store. Its slot locks are written in the same
        // update with no expiry (see below), so the hold never lapses.
        status: 'confirmed',
        // What the guest grid keys on to suppress the booker initials, and
        // the flag to filter club-held hours out by later. Absent entirely on
        // a booking made for a named guest, as it always was.
        ...(guest.adminBlock ? { adminBlock: true } : {}),
        createdAt,
      };
    });

    // The records and their slot locks go out as ONE atomic write. A taken
    // hour fails the whole save with nothing written, and because the rules
    // see the 'confirmed' record in that same write, the locks carry no
    // expiry from the start — a confirmed booking's hold never lapses.
    const { granted, conflicts } = await claimSlotsAtomically(
      db,
      dateRecords.map(rec => ({ dateStr: rec.dateStr, timeSlots: rec.timeSlots, bookingId: rec.bookingId })),
      null,
      writes,
    );
    if (!granted) {
      const detail = conflicts
        .map(c => `${getCourtLabel(config, c.court)} at ${formatTimeSlotRange(c.timeSlot)}`)
        .join(', ');
      return { ok: false, reason: `${detail || 'One of those hours'} was just booked by someone else.` };
    }

    const primary = writes[`bookings/${dateRecords[0].bookingId}`];
    // Email is optional on admin-created bookings (a walk-in often has no
    // address), so only send when there's somewhere to send it. One email for
    // the reservation, not one per hour.
    if (primary.email) {
      sendConfirmationEmail(primary, config?.adminEmail || '', false, config).catch(() => {});
    }
    // Still "booking submitted" — an admin taking a walk-in or phone booking
    // is the same action a guest's own checkout does, just entered on the
    // guest's behalf. performedBy tells the two apart in the log. One entry
    // for the reservation, keyed on the group so a multi-date booking
    // resolves to a single booking in the trail.
    logAuditEvent({
      actionType: AUDIT_ACTION_TYPES.BOOKING_SUBMITTED,
      entityType: ENTITY_TYPES.BOOKING,
      entityId: groupId || dateRecords[0].bookingId,
      statusNew: 'confirmed',
      summary: markPastDateEdit(buildBookingAuditSummary({
        userName: guest.userName,
        confirmationId,
        sportId,
        dates: dateRecords.map(rec => ({ date: rec.dateStr, timeSlots: rec.timeSlots })),
      }), dateRecords.map(rec => rec.dateStr)),
    });
    return { ok: true };
  };

// Add these lines outside the component to handle fullscreen functionality
function openFullscreen(imgUrl) {
  const fullScreenImg = document.createElement('img');
  fullScreenImg.src = imgUrl;
  fullScreenImg.style.position = 'fixed';
  fullScreenImg.style.top = '0';
  fullScreenImg.style.left = '0';
  fullScreenImg.style.width = '100%';
  fullScreenImg.style.height = '100%';
  fullScreenImg.style.objectFit = 'cover';
  fullScreenImg.style.zIndex = '9999';

  document.body.appendChild(fullScreenImg);

  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = 'X';
  closeBtn.style.position = 'fixed';
  closeBtn.style.top = '10px';
  closeBtn.style.right = '10px';
  closeBtn.style.zIndex = '9999';
  closeBtn.style.backgroundColor = '#ff6b6b';
  closeBtn.style.color = '#fff';
  closeBtn.style.border = 'none';
  closeBtn.style.borderRadius = '50%';
  closeBtn.style.width = '40px';
  closeBtn.style.height = '40px';
  closeBtn.style.fontSize = '20px';
  closeBtn.style.transition = 'transform 0.3s ease';

  // Keyframes for bounce animation
  const keyframes = `
    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
      }
      40% {
        transform: translateY(-20px);
      }
      60% {
        transform: translateY(-10px);
      }
    }
  `;

  // Append the keyframes to the head
  const style = document.createElement('style');
  style.innerHTML = keyframes;
  document.head.appendChild(style);

  closeBtn.style.animation = 'bounce 1s infinite';

  closeBtn.onclick = () => {
    document.body.removeChild(fullScreenImg);
    document.body.removeChild(closeBtn);
  };

  closeBtn.onmouseenter = () => {
    closeBtn.style.transform = 'scale(1.2)';
  };

  closeBtn.onmouseleave = () => {
    closeBtn.style.transform = 'scale(1)';
  };

  document.body.appendChild(closeBtn);
}

  return (
    <div className="admin-booking">
      <h2 className="section-title">📅 Booking Management</h2>

      {/* Navigation Tabs */}
      <div className="admin-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '1rem', borderBottom: `1px solid ${COLORS.line}`, paddingBottom: '8px' }}>
        <button
          className={`tab-btn ${activeTab === 'grid' ? 'active' : ''}`}
          onClick={() => setActiveTab('grid')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'grid' ? COLORS.cyanDeep : COLORS.surface,
            color: activeTab === 'grid' ? '#ffffff' : COLORS.ink,
            border: activeTab === 'grid' ? `1px solid ${COLORS.cyanDeep}` : `1px solid ${COLORS.line}`,
            borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', fontFamily: "'Archivo', sans-serif", transition: 'all 0.2s ease'
          }}
        >
          📅 Grid View
        </button>
        <button
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'list' ? COLORS.cyanDeep : COLORS.surface,
            color: activeTab === 'list' ? '#ffffff' : COLORS.ink,
            border: activeTab === 'list' ? `1px solid ${COLORS.cyanDeep}` : `1px solid ${COLORS.line}`,
            borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', fontFamily: "'Archivo', sans-serif", transition: 'all 0.2s ease'
          }}
        >
          📋 Reservations ({filteredBookings.length})
        </button>
        {/* Cleanup Booking — records that exist in the database but were
            never submitted through a screen (see AdminCleanupBooking). No
            count in the label: knowing it would mean running the whole
            audit-trail comparison on every visit to Booking Management,
            for a screen that is opened rarely and deliberately. */}
        <button
          className={`tab-btn ${activeTab === 'cleanup' ? 'active' : ''}`}
          onClick={() => setActiveTab('cleanup')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'cleanup' ? COLORS.cyanDeep : COLORS.surface,
            color: activeTab === 'cleanup' ? '#ffffff' : COLORS.ink,
            border: activeTab === 'cleanup' ? `1px solid ${COLORS.cyanDeep}` : `1px solid ${COLORS.line}`,
            borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', fontFamily: "'Archivo', sans-serif", transition: 'all 0.2s ease'
          }}
        >
          Cleanup Booking
        </button>
      </div>

      {/* Grid View — "Manage availability" (jassen UI): date carousel,
          per-hour status grid, Reservation Queue sidebar. Date selection
          now lives inside AdminTimeSlotSelection's own calendar bar. */}
      {activeTab === 'grid' && (
        <AdminTimeSlotSelection
          ref={gridRef}
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          bookedSlots={bookedSlots}
          slotOverrides={slotOverrides}
          nextDayBookedSlots={nextDayBookedSlots}
          nextDaySlotOverrides={nextDaySlotOverrides}
          slotCloseReasons={slotCloseReasons}
          nextDaySlotCloseReasons={nextDaySlotCloseReasons}
          pendingQueue={pendingQueue}
          onManageBooking={handleCellClick}
          onSaveChanges={handleSaveGridChanges}
          onQueueCardClick={(booking) => setSelectedQueueBooking(booking)}
        />
      )}

      {activeTab === 'list' && (
        <div style={{ marginBottom: '1rem' }}>
          {/* Date Range Picker */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: '1rem',
            padding: '1rem',
            background: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
            borderRadius: '10px'
          }}>
            <span style={{ color: COLORS.inkSoft, fontSize: '0.9rem' }}>Filter by date:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.8rem', color: COLORS.inkSoft }}>From:</label>
              <input
                type="date"
                value={format(dateRange.startDate, 'yyyy-MM-dd')}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: new Date(e.target.value) }))}
                style={{
                  padding: '0.5rem',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.line}`,
                  background: '#fff',
                  color: COLORS.ink,
                  fontSize: '0.85rem'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.8rem', color: COLORS.inkSoft }}>To:</label>
              <input
                type="date"
                value={format(dateRange.endDate, 'yyyy-MM-dd')}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: new Date(e.target.value) }))}
                min={format(dateRange.startDate, 'yyyy-MM-dd')}
                style={{
                  padding: '0.5rem',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.line}`,
                  background: '#fff',
                  color: COLORS.ink,
                  fontSize: '0.85rem'
                }}
              />
            </div>
            <span style={{ fontSize: '0.8rem', color: COLORS.cyanDeep, fontWeight: 600 }}>
              ({filteredBookings.length} bookings found)
            </span>
          </div>

          <AdminBookingsList
            allBookings={filteredBookings}
            selectedDate={dateRange.startDate}
            dateRange={dateRange}
            neonLimeColor={COLORS.cyanDeep}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
            onBookingClick={(booking) => { handleListBookingClick(booking); }}
          />
        </div>
      )}

      {/* Cleanup Booking — reads nothing until the admin asks for a range,
          so mounting it costs nothing beyond the component itself. */}
      {activeTab === 'cleanup' && <AdminCleanupBooking />}

      {/* Action Modals */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
              height: 'auto',
              maxWidth: '400px',
              width: '95%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <h3 style={{ flexShrink: 0, marginBottom: '12px', fontSize: '1rem' }}>
              {modalType === 'new' && 'Book New Slot'}
              {modalType === 'existing' && `Manage Booking - ${selectedBooking?.booking?.userName}`}
            </h3>

            {modalType === 'new' && (
              <div className="modal-form" style={{ overflowY: 'auto' }}>
                {/* formatTimeSlotRange, not the raw config label — renders
                    "11:00-12:00 NN" rather than "11-12PM", matching the
                    grid and every other screen. Court uses its configured
                    name where the admin has set one. */}
                <p style={{ fontSize: '0.85rem' }}><strong>Time:</strong> {selectedBooking?.timeSlot ? formatTimeSlotRange(selectedBooking.timeSlot) : ''}</p>
                <p style={{ fontSize: '0.85rem', marginBottom: '1rem' }}><strong>Court:</strong> {selectedBooking?.court != null ? getCourtLabel(config, selectedBooking.court) : ''}</p>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Full Name:</label>
                  <input type="text" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} placeholder="Enter full name" style={{ fontSize: '0.85rem', padding: '0.5rem' }} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Email: <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional)</span></label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Enter email" style={{ fontSize: '0.85rem', padding: '0.5rem' }} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Phone: <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional)</span></label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Enter phone number" style={{ fontSize: '0.85rem', padding: '0.5rem' }} />
                </div>
                <div className="modal-actions">
                  {/* Stages only — nothing is written until "Save changes"
                      on the grid, so there's no async/loading state here. */}
                  <button className="btn-primary" onClick={handleCreateBooking} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}>Save</button>
                  <button className="btn-secondary" onClick={() => setShowModal(false)} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}>Cancel</button>
                </div>
              </div>
            )}

            {modalType === 'existing' && selectedBooking?.booking && (
              <div className="modal-form" style={{ overflowY: 'auto', flex: 1, paddingRight: '8px' }}>
                <p style={{ fontSize: '0.85rem' }}><strong>Time:</strong> {selectedBooking.timeSlot}</p>
                <p style={{ fontSize: '0.85rem' }}><strong>Court:</strong> {selectedBooking.court}</p>
                <p style={{ fontSize: '0.85rem' }}><strong>User:</strong> {selectedBooking.booking.userName}</p>
                <p style={{ fontSize: '0.85rem' }}><strong>Email:</strong> {selectedBooking.booking.email}</p>
                <p style={{ fontSize: '0.85rem' }}><strong>Phone:</strong> {selectedBooking.booking.phone}</p>
                <p style={{ fontSize: '0.85rem' }}><strong>Status:</strong> {formatBookingStatus(selectedBooking.booking.status)}</p>

                {/* What is still owed, on the reservation an admin is most
                    likely to be looking at when a guest turns up to pay the
                    rest. Absent entirely on a fully-paid booking — a line
                    reading "balance ₱0" on every other reservation would
                    train staff to stop reading it. */}
                {selectedPaymentState.isPartial && (
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      background: 'rgba(214, 138, 16, .10)',
                      border: '1px solid rgba(214, 138, 16, .35)',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 700, color: COLORS.warning }}>
                      {PARTIAL_PAYMENT_BADGE} — not yet fully paid
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: COLORS.inkSoft }}>
                      Paid {formatMoney(selectedPaymentState.amountPaid, config)} ·
                      {' '}Balance {formatMoney(selectedPaymentState.balanceDue, config)}
                    </p>
                  </div>
                )}

                {/* Receipt Section */}
                {hasReceipt(selectedBooking.booking.originalData) && (
                  <div className="receipt-section" style={{ marginTop: '10px', padding: '8px', background: COLORS.cardBg, borderRadius: '6px' }}>
                    <p style={{ marginBottom: '8px', fontSize: '0.8rem' }}>
                      <strong>📎 Payment Receipt</strong>
                    </p>

                    {!showReceipt ? (
                      <button
                        onClick={handleViewReceipt}
                        style={{
                          padding: '6px 12px',
                          background: '#1877f2',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: 'white',
                          fontSize: '0.75rem'
                        }}
                      >
                        View receipt
                      </button>
                    ) : (
                      <div className="receipt-viewer">
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <button
                            onClick={() => setShowReceipt(false)}
                            style={{
                              padding: '4px 8px',
                              background: '#ff6b6b',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.7rem'
                            }}
                          >
                            Hide
                          </button>
                        </div>
                        {receiptLoading && loadingSpinner('Loading receipt')}
                        {!receiptLoading && !receiptImage && (
                          <p style={{ fontSize: '0.7rem', color: COLORS.inkSoft, textAlign: 'center' }}>
                            This receipt could not be loaded. Please try again.
                          </p>
                        )}
                        {!receiptLoading && receiptImage && (
                          <>
                            <img
                              src={receiptImage}
                              alt="Payment Receipt"
                              onClick={() => setFullscreenImage(receiptImage)}
                              style={{
                                width: '100%',
                                maxHeight: '150px',
                                objectFit: 'contain',
                                borderRadius: '6px',
                                border: `2px solid ${COLORS.cyanDeep}`,
                                cursor: 'pointer',
                              }}
                            />
                            <p style={{ fontSize: '0.65rem', color: COLORS.inkSoft, marginTop: '6px', textAlign: 'center' }}>
                              Click image to view fullscreen
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* A finished reservation stays fully READABLE — the admin
                    still needs to look one up, check the receipt, read the
                    guest's details. Only the buttons that would change it go
                    away, replaced by the reason so the missing Cancel reads
                    as deliberate rather than as a broken screen. */}
                {!canActOnBooking(selectedBooking.booking.originalData || selectedBooking.booking) && (
                  <p style={{ fontSize: '0.75rem', color: COLORS.inkSoft, marginTop: '1rem', lineHeight: 1.5 }}>
                    This reservation has finished — its dates have passed, so it can no longer be
                    confirmed, declined or cancelled here.
                  </p>
                )}
                <div className="modal-actions" style={{ marginTop: '1rem' }}>
                  {canActOnBooking(selectedBooking.booking.originalData || selectedBooking.booking) && (
                    <>
                      {selectedBooking.booking.status === 'pending' && (
                        <>
                          <button className="btn-primary btn-confirm" onClick={handleConfirmBooking} disabled={loading} style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>{loading ? '...' : 'Confirm'}</button>
                          {/* Only where the club actually accepts one —
                              with no percentage configured this is not an
                              option staff should be offered. */}
                          {partialEnabled && (
                            <button
                              className="btn-primary btn-partial"
                              onClick={() => handleOpenPartialPayment('grid')}
                              disabled={loading}
                              style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}
                              title={`Confirm against a downpayment of at least ${partialPercent}% of the total`}
                            >
                              Confirm (Partial)
                            </button>
                          )}
                          <button className="btn-primary btn-disapprove" onClick={handleDisapproveBooking} disabled={loading} style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>{loading ? '...' : 'Disapprove'}</button>
                        </>
                      )}
                      {/* A partially-paid booking is already confirmed, so
                          it never comes back through the pending branch
                          above and needs its own button. Withheld once the
                          reservation is off — collecting the balance on a
                          cancelled or declined booking is not a thing staff
                          should be able to do by mistake. */}
                      {selectedPaymentState.isPartial
                        && selectedBooking.booking.status !== 'cancelled'
                        && selectedBooking.booking.status !== 'disapproved' && (
                        <button
                          className="btn-primary btn-settle"
                          onClick={handleMarkFullyPaid}
                          disabled={loading}
                          style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}
                          title="Record the remaining balance as received"
                        >
                          {loading ? '...' : 'Mark Fully Paid'}
                        </button>
                      )}
                      {selectedBooking.booking.status === 'confirmed' && (
                        /* Opened from a grid cell, this releases just THAT
                           hour and only stages it — the grid's Save changes
                           button writes it, so a mis-click is undoable.
                           Opened from the reservations list there is no cell
                           and no save bar, so it stays what it was: the whole
                           reservation, cancelled on the spot. */
                        selectedBooking.fromGrid ? (
                          <button
                            className="btn-primary btn-cancel"
                            onClick={handleStageCancelHour}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}
                            title="Frees this hour on this court. Press Save changes on the grid to apply it."
                          >
                            Cancel This Hour
                          </button>
                        ) : (
                          <button className="btn-primary btn-cancel" onClick={handleCancelBooking} disabled={loading} style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>{loading ? '...' : 'Cancel Booking'}</button>
                        )
                      )}
                    </>
                  )}
                  <button className="btn-secondary" onClick={() => setShowModal(false)} style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem' }}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedQueueBooking && (
        <AdminReservationDetailModal
          booking={selectedQueueBooking}
          loading={queueActionLoading}
          onClose={() => setSelectedQueueBooking(null)}
          onConfirm={handleQueueConfirm}
          onDecline={handleQueueDecline}
          // Absent unless the club takes downpayments, which is how the
          // card decides whether to offer the third button at all.
          onPartial={partialEnabled ? () => handleOpenPartialPayment('queue') : null}
          partialPercent={partialPercent}
          locked={!canActOnBooking(selectedQueueBooking)}
        />
      )}

      {/* Sits above whichever of the two modals opened it — neither is
          closed until the payment is written, so cancelling puts the admin
          back on the reservation they were reading. */}
      <PartialPaymentModal
        isOpen={!!partialTarget}
        guestName={partialTarget?.guestName || ''}
        total={partialTarget?.total || 0}
        percent={partialPercent}
        resolvingTotal={!!partialTarget?.resolving}
        saving={partialSaving}
        onSubmit={handleConfirmPartialPayment}
        onClose={() => setPartialTarget(null)}
      />

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        confirmText={modalConfig.confirmText}
        onConfirm={modalConfig.onConfirm}
        showCancel={modalConfig.showCancel}
        cancelText={modalConfig.cancelText}
      />

      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            cursor: 'pointer',
          }}
          onClick={() => setFullscreenImage(null)}
        >
          <button
            onClick={() => setFullscreenImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '24px',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
          <img
            src={fullscreenImage}
            alt="Fullscreen Receipt"
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default AdminBooking;